import React, { useState, useEffect, useCallback, useRef } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import "./Map.css";
import "./Legend.css";
import "./Slideshow.css";

const LoadingOverlay = () => (
  <div className="loading-overlay">
    <div className="loading-spinner"></div>
    <p>Loading...</p>
  </div>
);

const containerStyle = {
  width: "100%",
  height: "700px",
};

const center = {
  lat: -37.8136,
  lng: 144.9631,
};

const mapOptions = {
  zoom: 11,
  mapId: "59f137c98e16b358",
};

function MyMap() {
  const [placeIdToColorMap, setPlaceIdToColorMap] = useState(new Map());
  const crimeDataMapRef = useRef(new Map());
  const [selectedCategory, setSelectedCategory] =
    useState("rate_total_offence");
  const mapRef = useRef(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [isLoadingMarkers, setIsLoadingMarkers] = useState(false);

  // Load the Google Maps JavaScript API script
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyBmXc5jBIHoFm5Qdwf88B1ApGGpR_kCSZo",
    libraries: ["places", "geometry"],
  });

  // Calculate colors for each suburb based on the selected crime category
  const calculateColors = useCallback(
    (data) => {
      const colorLevels = [
        "#00ff00",
        "#66ff00",
        "#ccff00",
        "#ffff00",
        "#ffcc00",
        "#ff9900",
        "#ff6600",
        "#ff3300",
        "#ff0000",
      ];

      const crimeRates = data
        .filter((item) => Number(item.population) > 500)
        .map((item) => Number(item[selectedCategory]))
        .filter((rate) => !isNaN(rate));

      const minRate = Math.min(...crimeRates);
      const maxRate = Math.max(...crimeRates);

      const newPlaceIdToColorMap = new Map();
      const newCrimeDataMap = new Map();

      data.forEach((item) => {
        if (Number(item.population) > 500) {
          const crimeRate = Number(item[selectedCategory]);
          if (!isNaN(crimeRate)) {
            // Normalize the crime rate between 0 and 1
            const normalizedRate = (crimeRate - minRate) / (maxRate - minRate);
            const colorIndex = Math.floor(
              normalizedRate * (colorLevels.length - 1)
            );
            const color = colorLevels[colorIndex];

            newPlaceIdToColorMap.set(item.place_id, color);
            newCrimeDataMap.set(item.place_id, crimeRate);
          }
        }
      });

      setPlaceIdToColorMap(newPlaceIdToColorMap);
      crimeDataMapRef.current = newCrimeDataMap;
    },
    [selectedCategory]
  );

  // Fetch crime data from the API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://nxs4mvj68d.execute-api.ap-southeast-2.amazonaws.com/prod/crimedata"
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const responseData = await response.json();

        if (responseData.statusCode === 200 && responseData.body) {
          const data = JSON.parse(responseData.body);
          if (Array.isArray(data) && data.length > 0) {
            calculateColors(data);
            setIsDataLoaded(true);
          } else {
            console.error("Data is empty or not an array:", data);
          }
        } else if (responseData.statusCode >= 400) {
          console.error("API Error:", responseData.body);
        } else {
          console.error("Data is not in the expected format:", responseData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [calculateColors]);

  // Handle marker click event
  const handleMarkerClick = useCallback((listing) => {
    setSelectedListing(listing);
  }, []);

  // Handle suburb click event
  const handleClick = useCallback(async (event) => {
    setIsLoadingMarkers(true);
    const feature = event.features[0];
    if (feature) {
      const place = await feature.fetchPlace();
      if (place.displayName) {
        const suburbName = place.displayName
          .split(",")[0]
          .replace(" ", "")
          .toLowerCase();

        const rentalListings = await fetchRentalListings(suburbName);

        const validMarkers = rentalListings
          .filter(
            (listing) =>
              listing.address &&
              listing.address.location &&
              listing.address.location.latitude &&
              listing.address.location.longitude
          )
          .map((listing) => ({
            position: {
              lat: listing.address.location.latitude,
              lng: listing.address.location.longitude,
            },
            title: listing.title,
            listing,
          }));

        setMarkers(validMarkers);

        createInfoWindow(event, feature);
      } else {
        console.error("place.displayName is undefined or empty");
      }
    }
    setIsLoadingMarkers(false);
  }, []);

  // Create an info window for the clicked suburb
  const createInfoWindow = useCallback(async (event, feature) => {
    if (!feature.placeId) return;

    const place = await feature.fetchPlace();
    const crimeRate = crimeDataMapRef.current.get(feature.placeId);
    const content = `
        <div class="info-window">
        <h3 class="info-window-title">${place.displayName}</h3>
        <p class="info-window-text">Crime Rate: ${
          crimeRate ? crimeRate.toFixed(3) : "N/A"
        }</p>
      </div>
      `;

    const infoWindow = new window.google.maps.InfoWindow({
      content: content,
      position: event.latLng,
    });

    infoWindow.open(mapRef.current);

    mapRef.current.panTo(event.latLng);
    mapRef.current.setZoom(14);

    setTimeout(() => {
      infoWindow.close();
    }, 2000);
  }, []);

  // Load the map
  const onLoad = useCallback(
    (map) => {
      mapRef.current = map;

      const ddsLayer = map.getFeatureLayer("LOCALITY");
      ddsLayer.addListener("click", handleClick);

      ddsLayer.style = (params) => {
        const currentPlaceId = params.feature.placeId;
        const suburbColor = placeIdToColorMap.get(currentPlaceId) || "#CCCCCC";
        return {
          fillColor: suburbColor,
          fillOpacity: 0.6,
          strokeColor: "black",
          strokeOpacity: 1.0,
          strokeWeight: 0.75,
        };
      };
    },
    [placeIdToColorMap, handleClick]
  );

  // Update the map style when the selected crime category changes
  useEffect(() => {
    if (mapRef.current) {
      const ddsLayer = mapRef.current.getFeatureLayer("LOCALITY");
      ddsLayer.style = (params) => {
        const currentPlaceId = params.feature.placeId;
        const suburbColor = placeIdToColorMap.get(currentPlaceId) || "#CCCCCC";
        return {
          fillColor: suburbColor,
          fillOpacity: 0.6,
          strokeColor: "black",
          strokeOpacity: 1.0,
          strokeWeight: 0.75,
        };
      };
    }
  }, [placeIdToColorMap, selectedCategory]);

  if (!isLoaded || !isDataLoaded) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="map-container">
      {isLoadingMarkers && <LoadingOverlay />}
      <div className="filter-card">
        <h4>Filter Map by Crime Category</h4>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="rate_total_offence">All Crime Rate</option>
          <option value="rate_crimes_against_the_person">
            Stalking and Harassment Rate
          </option>
          <option value="rate_property_and_deception_offences">
            Burglary and Theft Rate
          </option>
          <option value="rate_drug_offences">Drug Offence Rate</option>
          <option value="rate_other_offences">Sexual Offence Rate</option>
          <option value="total_offence">Crime Sum(Last 10 years)</option>
        </select>
        {isLoadingMarkers && <div className="loading-markers">Loading...</div>}
        {selectedListing && (
          <div className="listing-details">
            <h4 className="listing-title">{selectedListing.title}</h4>
            <div className="slideshow">
              {selectedListing.images.map((image, index) => (
                <img
                  key={index}
                  src={`${image.server}/400x400${image.uri}`}
                  alt={`Slide ${index + 1}`}
                  className="slide"
                  onClick={() =>
                    window.open(`${image.server}/800x800${image.uri}`, "_blank")
                  }
                  style={{ cursor: "pointer" }}
                />
              ))}
            </div>
            <div className="listing-info">
              <p>
                <strong>Address:</strong>{" "}
                {selectedListing.address.streetAddress}
              </p>
              <p>
                <strong>Bedrooms:</strong>{" "}
                {selectedListing.features.general.bedrooms}
              </p>
              <p>
                <strong>Bathrooms:</strong>{" "}
                {selectedListing.features.general.bathrooms}
              </p>
              <p>
                <strong>Price:</strong> {selectedListing.price.display}
              </p>
              <p>
                <strong>Agency:</strong> {selectedListing.agency.name}
              </p>
              <p>
                <strong>Phone:</strong> {selectedListing.agency.phoneNumber}
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="map-card">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={mapOptions.zoom}
          onLoad={onLoad}
          options={{ mapId: mapOptions.mapId }}
        >
          {markers.map((marker, index) => (
            <Marker
              key={index}
              position={marker.position}
              title={marker.title}
              onClick={() => handleMarkerClick(marker.listing)}
            />
          ))}
        </GoogleMap>
      </div>
      <div className="legend-container">
        <div className="legend-title">Crime Rate</div>
        <div className="legend-gradient"></div>
        <div className="legend-labels">
          <span>Low</span>
          <span>High</span>
        </div>
      </div>
    </div>
  );
}

async function fetchRentalListings(suburbName) {
  const response = await fetch(
    `https://realty-in-au.p.rapidapi.com/properties/list?page=1&pageSize=30&sortType=relevance&channel=rent&searchLocation=${suburbName}&searchLocationSubtext=Region&type=region`,
    {
      headers: {
        "X-Rapidapi-Key": "680eb2d190msh5bfd7734e2fb6bap1c81c6jsnd1140dc1089a",
        "X-Rapidapi-Host": "realty-in-au.p.rapidapi.com",
        Host: "realty-in-au.p.rapidapi.com",
      },
    }
  );
  const data = await response.json();
  return data.tieredResults[0].results;
}

export default MyMap;
